// No longer loading from a static JSON file. Data will be fetched from live APIs.

// Configuration for Adapt instances
// Simplified configuration focusing on instance details for navigation
const ADAPT_INSTANCES = {
  'adapt-dev': {
    name: 'Adapt Dev',
    url: 'https://adapt-dev.laerdal.io',
    description: 'Development test environment for course creation'
  },
  'adapt-tryout': {
    name: 'Adapt Tryout',
    url: 'https://adapt-tryout-test.laerdal.io',
    description: 'Tryout test environment for course creation'
  },
  'adapt-src': {
    name: 'Adapt SRC',
    url: 'https://adapt-src.laerdal.io/classic',
    description: 'An instance dedicated to developing eLearning courses for the Norwegian and Swedish Resuscitation Councils.'
  },
  'adapt-la': {
    name: 'Adapt LA',
    url: 'https://adapt-la.laerdal.io/classic',
    description: 'An instance dedicated to development for individual course creators who are not part of any product team or belong to corporate functions.'
  },
  'adapt-lgh': {
    name: 'Adapt LGH',
    url: 'https://adapt-lgh.laerdal.io/classic',
    description: 'An instance dedicated to developing eLearning courses for Laerdal Global Health.'
  },
  'adapt-sd': {
    name: 'Adapt SD',
    url: 'https://adapt-sd.laerdal.io/classic',
    description: 'An instance dedicated to developing eLearning courses for Strategic Design, Technical Services and Educational Services.'
  },
  'adapt-cph': {
    name: 'Adapt CPH',
    url: 'https://adapt-cph.laerdal.io/classic',
    description: 'An instance dedicated to developing eLearning courses for Copenhagen based products.'
  },
  'adapt-who': {
    name: 'Adapt WHO',
    url: 'https://adapt-who.laerdal.io/#dashboard',
    description: 'An instance dedicated to developing eLearning courses for World Health Organization.'
  }
};

class AdaptUserService {
  constructor() {
    this.instances = ADAPT_INSTANCES;
  }

  /**
   * Verify user access by querying live Adapt instances.
   * This method queries each Adapt instance's API to check for user access.
   * @param {string} email - User's email address
   * @returns {Promise<Array>} Array of accessible instances
   */
  async getUserAccessibleInstances(email) {
    console.log('\n📂 [AdaptUserService] Starting live API verification...');
    console.log('📧 [AdaptUserService] Email:', email);

    const normalizedEmail = email.toLowerCase().trim();
    console.log('📧 [AdaptUserService] Normalized email:', normalizedEmail);

    const accessibleInstances = [];
    const promises = [];

    for (const [instanceKey, instanceData] of Object.entries(this.instances)) {
      const apiUrl = `https://${instanceKey}.laerdal.io/api/userDetails?search[email]=${encodeURIComponent(normalizedEmail)}`;
      
      promises.push(
        fetch(apiUrl)
          .then(response => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
          })
          .then(users => {
            if (users && users.length > 0) {
              const user = users[0]; // Assuming the first user is the correct one
              console.log(`✅ [AdaptUserService] User found in ${instanceKey}`);
              accessibleInstances.push({
                id: instanceKey,
                name: instanceData.name || instanceKey,
                url: instanceData.url,
                userRole: user.roles ? user.roles.map(r => r.name).join(', ') : 'User',
                authType: user.auth || 'unknown',
                lastAccess: user.lastAccess,
                userId: user._id,
                source: 'live-api'
              });
            }
          })
          .catch(error => {
            console.error(`❌ [AdaptUserService] Error fetching from ${instanceKey}:`, error.message);
          })
      );
    }

    await Promise.all(promises);

    console.log(`📊 [AdaptUserService] Live API verification complete: Found ${accessibleInstances.length} accessible instances`);
    return accessibleInstances;
  }

  /**
   * Verify user access using live API queries
   * @param {string} email - User's email from SSO or SAP CDC login
   * @param {string} accessToken - Authentication token (for compatibility)
   * @returns {Promise<Object>} Verification result with accessible instances
   */
  async verifyUserAccess(email, accessToken) {
    console.log('\n🔐 [AdaptUserService.verifyUserAccess] Starting live API-based user verification...');
    console.log('📧 [AdaptUserService.verifyUserAccess] Email:', email);
    console.log('🎟️ [AdaptUserService.verifyUserAccess] Access token length:', accessToken ? accessToken.length : 0);
    console.log('⏰ [AdaptUserService.verifyUserAccess] Verification timestamp:', new Date().toISOString());
    console.log('🚀 [AdaptUserService.verifyUserAccess] Using live API queries');

    try {
      // Get instances user has access to from live APIs
      console.log('🚀 [AdaptUserService.verifyUserAccess] Querying live APIs...');
      const accessibleInstances = await this.getUserAccessibleInstances(email);

      const verificationResult = {
        isValid: accessibleInstances.length > 0,
        email: email,
        accessibleInstances: accessibleInstances,
        totalInstances: accessibleInstances.length,
        verifiedAt: new Date().toISOString(),
        queryMethod: 'Live API Query',
        dataSource: {
          totalInstancesQueried: Object.keys(this.instances).length
        }
      };

      console.log('📊 [AdaptUserService.verifyUserAccess] Final verification result:', {
        isValid: verificationResult.isValid,
        email: verificationResult.email,
        totalInstances: verificationResult.totalInstances,
        queryMethod: verificationResult.queryMethod,
        verifiedAt: verificationResult.verifiedAt
      });

      if (verificationResult.isValid) {
        console.log('✅ [AdaptUserService.verifyUserAccess] User verification SUCCESSFUL');
        console.log('🎯 [AdaptUserService.verifyUserAccess] User has access to the following instances:');
        verificationResult.accessibleInstances.forEach((instance, index) => {
          console.log(`   ${index + 1}. ${instance.name} (${instance.id}) - Role: ${instance.userRole} - Auth: ${instance.authType}`);
        });
      } else {
        console.log('❌ [AdaptUserService.verifyUserAccess] User verification FAILED - No access found via live API query');
      }

      return verificationResult;
    } catch (error) {
      console.error('❌ [AdaptUserService.verifyUserAccess] Error during verification:', error);
      
      const errorResult = {
        isValid: false,
        email: email,
        accessibleInstances: [],
        totalInstances: 0,
        error: error.message,
        verifiedAt: new Date().toISOString(),
        queryMethod: 'Live API Query (Failed)'
      };
      
      console.log('💾 [AdaptUserService.verifyUserAccess] Returning error result:', errorResult);
      return errorResult;
    }
  }
}

const adaptUserService = new AdaptUserService();
export default adaptUserService;
